import React, { useCallback, useMemo, useRef, useState } from 'react';
import Modal from '../Modal/Modal';
import TMMButton from '../TMMButton';
import Form, { FormRefType } from '#/common/components/Form';
import If from '../If';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import styled from 'styled-components';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import type { InitializedEventInfo } from 'devextreme/events';
import type dxForm from 'devextreme/ui/form';

import { MenuItem } from '#/modules/workOrder/utils/bulk-edit';

const HyperLink = styled.span`
  color: #0f67ff;
  text-decoration: underline;
  padding-left: 6px;
  cursor: pointer;
`;

const MessageContainer = styled.div`
  font-family: 'Inter', Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  color: #151921; // custom color
  border: 1px solid #3176f6; // custom color
  background: #d1e1fd;
  border-radius: 16px;
  padding: 12px 24px;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
`;

// Sets min/max on the related date editor when the current one changes.
// Uses optional chaining so it is a safe no-op when the editor doesn't exist.
const applyDateMinMax = (
  form: dxForm,
  currentField: string,
  relatedField: string,
  minMax: 'min' | 'max',
) => {
  const raw = form.getEditor(currentField)?.option('value');
  const d = raw ? new Date(raw) : null;
  if (d) d.setHours(0, 0, 0, 0);
  form.getEditor(relatedField)?.option(minMax, d && !isNaN(d.getTime()) ? d : null);
};

export type ModalWithFormProps = {
  visible: boolean;
  selectedMenuItem: MenuItem | undefined;
  onClose: () => void;
  onSubmit: (formData?: any) => void;
  eligibleCount: number;
  selectedCount: number;
};

const ModalWithForm: React.FC<ModalWithFormProps> = ({
  visible,
  selectedMenuItem,
  onClose,
  onSubmit,
  eligibleCount,
  selectedCount,
}) => {
  const [showMore, setShowMore] = useState(false);
  const formRef = useRef<FormRefType | null>(null);
  const { isMobile } = useViewport();

  const handleContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const form = e.component;
    if (!form) return;
    applyDateMinMax(form, 'startDate', 'endDate', 'min');
    applyDateMinMax(form, 'endDate', 'startDate', 'max');
  }, []);

  const handleFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const form = e.component;
    if (e.dataField === 'startDate') applyDateMinMax(form, 'startDate', 'endDate', 'min');
    if (e.dataField === 'endDate') applyDateMinMax(form, 'endDate', 'startDate', 'max');
  }, []);

  const actions = [
    <TMMButton key="cancel" label="Cancel" type="normal" onClick={onClose} />,
    <TMMButton
      key="ok"
      label="Save Changes"
      type="submit"
      disabled={eligibleCount === 0}
      onClick={() => {
        const instance = formRef.current?.instance();
        const { isValid } = instance?.validate() as any;
        const formData = instance?.option('formData');

        if (!isValid) return;

        onSubmit(formData);
      }}
    />,
  ];

  const emptyRecord = useMemo(() => ({}), [visible, selectedMenuItem?.id]);

  const renderMessage = useCallback(() => {
    if (!selectedMenuItem?.messageTemplate) return null;
    const { baseBold, baseNormal, details } = selectedMenuItem.messageTemplate(
      eligibleCount,
      selectedCount,
    );
    return (
      <>
        <span style={{ fontWeight: 'bold' }}>{baseBold}</span>
        <span>{baseNormal}</span>
        <If condition={showMore && !!details}>
          <span>
            <br />
            <br />
            {details}
            <HyperLink onClick={() => setShowMore(false)}>See less</HyperLink>
          </span>
        </If>
        <If condition={!showMore && !!details}>
          <span>
            <HyperLink onClick={() => setShowMore(true)}>See more</HyperLink>
          </span>
        </If>
      </>
    );
  }, [emptyRecord, showMore, selectedMenuItem, eligibleCount, selectedCount]);

  return (
    <Modal
      visible={visible}
      title={selectedMenuItem?.label ?? ''}
      dragEnabled
      width={690}
      height="auto"
      onHiding={onClose}
      content={
        <div
          style={{
            paddingBottom: '100px',
            position: 'relative',
            height: isMobile ? 'calc(100vh - 190px)' : 'fit-content',
          }}>
          <Form
            key={`${selectedMenuItem?.id ?? 'none'}-${visible ? 'open' : 'closed'}`}
            ref={formRef}
            fields={selectedMenuItem?.fields ?? []}
            record={emptyRecord}
            onContentReady={handleContentReady}
            onFieldDataChanged={handleFieldDataChanged}
          />
          <MessageContainer>{renderMessage()}</MessageContainer>
        </div>
      }
      actions={actions}
    />
  );
};

export default React.memo(ModalWithForm);
